import React from "react";
import styled from "styled-components";

import Sparkline, { withTrailingMean } from "../common/Sparkline";
import analyticsLogo from "../AccountDetails/analytics-logo.png";
import searchConsoleLogo from "../WorkspaceAdmin/SearchConsoleSettings/search-console-logo.svg";
import { ANALYTICS, kpisOf } from "./kpis";

const LOGOS = { [ANALYTICS]: analyticsLogo };

/**
 * The site's figures over the date range, one tile each (kpis.js), each a toggle for drawing it on the
 * chart: `charted` the keys drawn, `onToggle(key)` when a tile is pressed. The hairlines between tiles
 * are each tile's right and bottom shadow on the box's white, clipped at its edge, so a short last row
 * leaves no grey cell; a tile has no background of its own, which would cover its neighbour's.
 */
export default function KpiBar({ traffic, range, charted, onToggle }) {
    return (
        <Bar>
            {kpisOf(traffic, range).map((kpi) => {
                const on = charted.includes(kpi.key);
                const days = kpi.days.map((day) => day.date);
                // The line's trailing 7-day mean, upside down for a figure where lower is better, so up is always better.
                const line = withTrailingMean(kpi.days.map((day) => (kpi.lowerIsBetter ? -1 : 1) * (kpi.daily(day) ?? 0)), days)
                    .map(({ day, avg }) => ({ day, v: avg }));
                return (
                    <Tile key={kpi.key} type="button" aria-pressed={on} disabled={kpi.value == null} $on={on} $color={kpi.color}
                          onClick={() => onToggle(kpi.key)} title={on ? `Hide ${kpi.label} from the chart` : `Show ${kpi.label} on the chart`}>
                        <LabelRow>
                            <Logo src={LOGOS[kpi.source] ?? searchConsoleLogo} alt={kpi.source} title={kpi.source} />
                            {kpi.label}
                            <Swatch $color={kpi.color} $on={on} />
                        </LabelRow>
                        <Value>{kpi.value == null ? "–" : kpi.format(kpi.value)}</Value>
                        {kpi.change && <Change $better={kpi.better}>{kpi.change} <Muted>vs prev. period</Muted></Change>}
                        <Line>{line.length > 1 && <Sparkline data={line} color={on ? kpi.color : "#cbd5e1"} />}</Line>
                    </Tile>
                );
            })}
        </Bar>
    );
}

const Bar = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
    margin-bottom: 18px;
    @media (max-width: 600px) {
        border-radius: 0;
        border-left: none;
        border-right: none;
    }
`;

/** A figure's colour along its top while it is drawn. */
const Tile = styled.button`
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
    padding: 16px 20px 12px;
    border: none;
    background: none;
    font: inherit;
    text-align: left;
    color: inherit;
    cursor: pointer;
    box-shadow: 1px 0 0 #e2e8f0, 0 1px 0 #e2e8f0;
    &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: ${({ $on, $color }) => ($on ? $color : "transparent")};
    }
    &:hover:not(:disabled) { background: #f8fafc; }
    &:focus-visible { outline: 2px solid #6366f1; outline-offset: -2px; }
    &:disabled { cursor: default; }
`;

const LabelRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #64748b;
`;

const Logo = styled.img`
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    object-fit: contain;
`;

/** The figure's colour, filled while drawn and hollow otherwise, so the tile names its line. */
const Swatch = styled.span`
    flex-shrink: 0;
    width: 8px;
    height: 8px;
    margin-left: auto;
    border-radius: 999px;
    border: 1.5px solid ${({ $color }) => $color};
    background: ${({ $on, $color }) => ($on ? $color : "transparent")};
`;

const Value = styled.div`
    font-size: 26px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: #0f172a;
    line-height: 1.15;
`;

const Change = styled.div`
    font-size: 12px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: ${({ $better }) => ($better === true ? "#079455" : $better === false ? "#D92D20" : "#64748b")};
`;

const Muted = styled.span`
    font-weight: 400;
    color: #64748b;
`;

const Line = styled.div`
    height: 28px;
    margin-top: 4px;
`;
